/**
 * L2 card — `Card`, the ONE generic card template.
 *
 * It used to be two — an item card and a session card — and they were
 * literally the same function: both delegated to one `BoundCard` with the same
 * default layout, the same props and the same single signal. The split was
 * copied from the COLLECTIONS, where it is real — a folded item and a capture
 * session have genuinely different identities and different deletes — but a
 * card owns NEITHER, so there was nothing for the second entry to mean. Naming
 * a card after the lifecycle of the thing above it is the collision this merge
 * undoes.
 *
 * The card is the generic template a collection lays out ONCE PER ROW under
 * the `item` scope. It is pure View: every field is a bind against that row,
 * the only signal it emits is `activated`, and it owns no identity — the
 * collection attaches `item_id` / `grid_id` when it re-emits.
 *
 * That split is what makes a new widget cost zero components: the widget
 * declares `{layout, media, title, subtitle, badges, meta}` in its blueprint
 * and reuses this file, instead of authoring a MedicineTile and a MealRow and
 * a PieceTile that differ only in which fields they read.
 *
 * A field may be a literal, "{interpolated}", a bare path ("item.name") or the
 * object form ({"$": "item.name"}); `bindValue` resolves all four against the
 * item-scoped context the grid opened.
 *
 * Props: { layout, media, motionSrc, title, subtitle, eyebrow, badges[],
 * meta[], aspect, scrim, overlayAlign }, where a `meta` entry is either
 * `{label?, value}` or `{meter, max?, segments?}`. The last four are the
 * `poster` layout's: the media fills the frame and the words are painted on it
 * (see `PosterFace`), and `motionSrc` is the ANIMATED cover that plays over the
 * still when the tile is on screen and the reader has not asked for less
 * motion (see `PosterMotion`).
 *
 * A `badges[]` entry is `{tone?, text?, icon?, dot?, when?, region?}`. `region`
 * names one of the four CORNERS (`top-start` · `top-end` · `bottom-start` ·
 * `bottom-end`) and pins the chip there over the card's own frame — no
 * `Overlay` wrapper, which is what a blueprint used to have to wrap the card in
 * (losing the card's layout to get one chip into a corner). `when` gates a
 * badge on a bound predicate, so "in season" appears on the rows that are and
 * on no others. `media.badge` takes the same shape and may be a LIST.
 */
import { useEffect, useRef, useState } from 'react';
import { Badge, Icon, Image } from './primitives';
import { Gauge } from './viz';
import {
  TYPE_STYLE, resetButton, ratioValue, toneColor, toneSoft, prefersReducedMotion,
  MEDIA_INK, MEDIA_INK_2, MEDIA_SCRIM, MEDIA_SCRIM_HEIGHT, OVERLAY_ALIGN, POSTER_ASPECT,
  BADGE_CORNER,
} from './ui';
import { evalWhen, num, resolveValue, isLocalPrivacySessionId, contractLifecycle } from '../paths';

/** One card field → its value under the current item scope. */
function bindValue(spec, ctx) {
  const v = resolveValue(spec, ctx);
  return v === undefined ? '' : v;
}

function text(spec, ctx) {
  const v = bindValue(spec, ctx);
  if (v == null || v === '') return '';
  return Array.isArray(v) ? v.filter(Boolean).join(', ') : String(v);
}

/** The keys that make a `media` object a SPEC rather than the src itself. */
const MEDIA_SPEC_KEYS = [
  'src', 'motionSrc', 'ratio', 'fit', 'badge', 'plate', 'alt',
  'fallback', 'fallbackKind', 'fallbackValue', 'fallbackColor', 'fallbackShape', 'fallbackTone',
  'position', 'inset', 'lift',
];

/**
 * `media` is either a bind (the src) or `{src, ratio, fit, badge, …}`.
 *
 * The key list matters: a spec that declares only a FALLBACK (a card whose rows
 * have no picture at all, drawing a `title` plate) carries no `src`, and
 * without its keys here the whole object was read AS the src.
 */
function mediaSpecOf(media) {
  if (media && typeof media === 'object' && !Array.isArray(media)
      && MEDIA_SPEC_KEYS.some((k) => media[k] !== undefined)) {
    return media;
  }
  return { src: media };
}

/**
 * One badge spec → the chip it draws, or null.
 *
 * TWO gates, and they are different questions. `when` asks whether this badge
 * BELONGS on this row at all — a bound predicate, so "in season" appears on the
 * three rows that are and on no others, without the blueprint minting a derive
 * whose only job is to blank out a string. The emptiness gate under it asks
 * whether the badge has anything to SAY: a bound text that resolved to nothing
 * is DROPPED rather than drawn empty, because "no status" must read as absence
 * and never as a blank chip.
 */
function badgeSpec(b, ctx) {
  if (!b || typeof b !== 'object') return null;
  if (b.when !== undefined && !evalWhen(b.when, ctx)) return null;
  const spec = {
    tone: text(b.tone, ctx) || undefined,
    text: text(b.text, ctx),
    icon: b.icon,
    dot: b.dot,
    region: BADGE_CORNER[b.region] ? b.region : null,
  };
  return spec.text || spec.dot || spec.icon ? spec : null;
}

function badgeSpecs(badges, ctx) {
  return (Array.isArray(badges) ? badges : [badges])
    .map((b) => badgeSpec(b, ctx))
    .filter(Boolean);
}

function BadgeChip({ spec }) {
  return <Badge variant={spec.dot && !spec.text ? 'dot' : 'pill'} props={spec} />;
}

/**
 * The IN-FLOW badge row — the caption's chips, unchanged.
 *
 * A badge that claimed a CORNER is not in this row: it is drawn by
 * `CornerBadges` over the card's own frame. Splitting them here (rather than
 * asking every layout to pass two lists) is what keeps a card that names no
 * region rendering byte-for-byte what it always did.
 */
function BadgeRow({ badges, ctx, style }) {
  const shown = badgeSpecs(badges, ctx).filter((b) => !b.region);
  if (!shown.length) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, minWidth: 0, ...style }}>
      {shown.map((b, i) => <BadgeChip key={`${b.tone || 'b'}-${b.text || i}`} spec={b} />)}
    </div>
  );
}

/**
 * A card is SESSION-BOUND + PRIVATE when the row it is bound to carries a
 * `session_id`/`source_session_id` that is a LOCAL-PRIVACY id (CONTRACT §10):
 * the phone mixes cloud + on-device(private) + widget rows in one feed, and this
 * marks which came from an on-device session. The row is the `item` scope a grid
 * opened per row, or `focusEntry` for a detail-bound card — the two roots the
 * card's own binds read (the Flutter twin is `_viewIrCardIsPrivate`).
 */
function cardIsPrivate(ctx) {
  const row = (ctx && (ctx.item || ctx.focusEntry)) || null;
  if (!row || typeof row !== 'object') return false;
  return isLocalPrivacySessionId(row.session_id ?? row.source_session_id);
}

/**
 * The four CORNERS of a card, each holding the badges that named it.
 *
 * Absolutely placed inside the card's own frame and inert to taps, so a corner
 * chip never steals the card's `activated` — the same rule `Overlay` carries
 * for its layers. Several badges in one corner stack as a row, in declared
 * order.
 *
 * `card` folds in the KIT-DEFAULT "Private" provenance chip (§10) — never
 * authored, drawn only at the CARD frame (not on the media's own corner layer),
 * so it positions, stacks and stays pointer-inert exactly like a declared
 * top-end badge.
 */
function CornerBadges({ badges, ctx, card = false, hasMedia = false }) {
  const shown = badgeSpecs(badges, ctx).filter((b) => b.region);
  // A private card WITH a hero image draws its Private chip on the MEDIA (see Media, bottom-end);
  // only a private card WITHOUT media falls back to a card-frame chip, still bottom-end.
  if (card && !hasMedia && cardIsPrivate(ctx)) {
    shown.push({ tone: 'muted', text: '🔒 Private', region: 'bottom-end' });
  }
  // KIT DEFAULT (Job-by-Contract V3e): a card bound to a contract row shows the ONE
  // authoritative entry-level lifecycle as a small pointer-inert corner mark in the
  // TOP-RIGHT — a peer of the Private badge, never authored. `contractLifecycle`
  // (paths.js) maps 1:1: queued → static grey dot · running → green breathing dot ·
  // error → red "!" · stopped → amber ◼ · delivered/none → no mark. A row without the
  // field falls back through that resolver to running/error/delivered (BC — the §13
  // no-log "!" and old contract data are unchanged). The row is the same scope the
  // Private badge reads (`ctx.item` per grid row, or `focusEntry` for a detail card);
  // pointer-inert, offset right when a declared top-end badge already sits there. The
  // Flutter twin is `_viewIrCardLifecycle` → the shared lifecycle corner badges.
  const lifecycle = card ? contractLifecycle((ctx && (ctx.item || ctx.focusEntry)) || null) : null;
  const lifecycleMark = lifecycle === 'queued' || lifecycle === 'running'
    || lifecycle === 'error' || lifecycle === 'stopped';
  if (!shown.length && !lifecycleMark) return null;
  const corners = new Map();
  for (const b of shown) {
    if (!corners.has(b.region)) corners.set(b.region, []);
    corners.get(b.region).push(b);
  }
  const out = [...corners.entries()].map(([region, list]) => (
    <div
      key={region}
      style={{
        position: 'absolute', ...BADGE_CORNER[region], zIndex: 2,
        display: 'flex', flexWrap: 'nowrap', gap: 4, pointerEvents: 'none', maxWidth: '92%',
      }}
    >
      {list.map((b, i) => <BadgeChip key={`${b.tone || 'b'}-${b.text || i}`} spec={b} />)}
    </div>
  ));
  if (lifecycleMark) {
    const topEndTaken = shown.some((b) => b.region === 'top-end');
    out.push(lifecycleCornerMark(lifecycle, topEndTaken));
  }
  return out;
}

/**
 * The lifecycle corner mark, drawn top-right, pointer-inert. `error` is the 16px
 * red "!" (unchanged §13 glyph); the other three are a 10px shape concentric with
 * it (its center is 14/14 from the corner, so a 10px shape lands at 9/9):
 *   running → GREEN dot, breathing (`viNativeBreath`, tokens.good)
 *   queued  → GREY dot, static (toneColor('muted') = --text-3)
 *   stopped → AMBER rounded square ◼ (toneColor('warn'), radius 2) — visibly NOT the "!"
 * `delivered`/null draw nothing (the card is the delivery). Offset right when a
 * declared top-end badge already occupies the corner. The `data-vi-card-*` hooks
 * are the twin-test selectors; the Flutter twin renders the same five shapes.
 */
function lifecycleCornerMark(lifecycle, topEndTaken) {
  if (lifecycle === 'error') {
    return (
      <div
        key="lifecycle-mark"
        data-vi-card-terminal="1"
        data-vi-card-lifecycle="error"
        style={{
          position: 'absolute', top: 6, right: topEndTaken ? 28 : 6, zIndex: 3,
          width: 16, height: 16, borderRadius: 8,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 12, fontWeight: 700, lineHeight: 1,
          color: '#fff', pointerEvents: 'none', background: toneColor('bad'),
        }}
      >!</div>
    );
  }
  const base = {
    position: 'absolute', top: 9, right: topEndTaken ? 31 : 9, zIndex: 3,
    width: 10, height: 10, pointerEvents: 'none',
  };
  if (lifecycle === 'running') {
    return (
      <div
        key="lifecycle-mark"
        data-vi-card-running="1"
        data-vi-card-lifecycle="running"
        style={{
          ...base, borderRadius: 5, background: toneColor('good'),
          animation: 'viNativeBreath 1.6s ease-in-out infinite',
        }}
      />
    );
  }
  if (lifecycle === 'queued') {
    return (
      <div
        key="lifecycle-mark"
        data-vi-card-queued="1"
        data-vi-card-lifecycle="queued"
        style={{ ...base, borderRadius: 5, background: toneColor('muted') }}
      />
    );
  }
  // stopped
  return (
    <div
      key="lifecycle-mark"
      data-vi-card-stopped="1"
      data-vi-card-lifecycle="stopped"
      style={{ ...base, borderRadius: 2, background: toneColor('warn') }}
    />
  );
}

/**
 * A meta entry is EITHER text (`{label?, value}`) or a GAUGE
 * (`{meter, max?, segments?}`) — the card's one slot for a bound n-of-m
 * reading, so a confidence level can ride the same declarative list as the
 * calorie line instead of needing a card variant of its own.
 *
 * A meter at zero is DROPPED, not drawn empty: `0 of 3` is the shape of "not
 * stated", and an unstated estimate must not look like a measured worst case.
 */
function metaRow(m, ctx) {
  if (!m || typeof m !== 'object') return null;
  const label = text(m.label, ctx);
  if (m.meter !== undefined) {
    const value = num(bindValue(m.meter, ctx));
    if (!(value > 0)) return null;
    return { kind: 'meter', label, value, max: m.max, segments: m.segments, tone: m.tone, variant: m.variant };
  }
  const value = text(m.value, ctx);
  return value ? { kind: 'text', label, value } : null;
}

function MetaRows({ meta, ctx }) {
  const rows = (Array.isArray(meta) ? meta : []).map((m) => metaRow(m, ctx)).filter(Boolean);
  if (!rows.length) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
      {rows.map((m, i) => (
        <div key={`${m.kind}-${m.label}-${m.value}-${i}`} style={{ display: 'flex', gap: 6, alignItems: 'center', minWidth: 0 }}>
          {m.label ? (
            <span style={{ ...TYPE_STYLE.caption, fontSize: 'var(--t-xs)', color: 'var(--text-3)', flex: '0 0 auto' }}>{m.label}</span>
          ) : null}
          {m.kind === 'meter' ? (
            <Gauge
              variant={m.variant || 'segments'}
              props={{ value: m.value, max: m.max, segments: m.segments, tone: m.tone, label: m.label || undefined }}
            />
          ) : (
            <span style={{
              ...TYPE_STYLE.caption, fontSize: 'var(--t-xs)', color: 'var(--text-2)',
              minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>{m.value}</span>
          )}
        </div>
      ))}
    </div>
  );
}

const TITLE_STYLE = {
  ...TYPE_STYLE.caption,
  fontSize: 'var(--t-sm)',
  fontWeight: 600,
  color: 'var(--text)',
  lineHeight: 1.25,
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
};

const SUBTITLE_STYLE = {
  ...TYPE_STYLE.caption,
  fontSize: 'var(--t-xs)',
  color: 'var(--text-3)',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const SUBTITLE_BODY_STYLE = {
  ...TYPE_STYLE.caption,
  fontSize: 'var(--t-sm)',
  color: 'var(--text-2)',
  lineHeight: 1.35,
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
};

/** `subtitleLines` > 1 turns the subtitle into a clamped body excerpt. */
function subtitleStyle(lines) {
  const n = Math.min(4, Math.max(1, Number(lines) || 1));
  return n > 1 ? { ...SUBTITLE_BODY_STYLE, WebkitLineClamp: n } : SUBTITLE_STYLE;
}

/** `action: {label, icon?, tone?}` → the resolved pill, or null when its label is empty. */
function actionSpec(action, ctx) {
  if (!action || typeof action !== 'object') return null;
  if (action.when !== undefined && !evalWhen(action.when, ctx)) return null;
  const label = text(action.label, ctx);
  return label ? { label, icon: action.icon, tone: action.tone } : null;
}

/**
 * The card's ONE tappable pill. It emits `actionRequested` and stops the tap
 * there, so the card's own `activated` (open the row) never fires with it.
 */
function ActionPill({ spec, emit }) {
  const fire = (e) => { e.stopPropagation(); emit?.('actionRequested', {}); };
  return (
    <span
      role="button"
      tabIndex={0}
      data-card-action=""
      aria-label={spec.label}
      onClick={fire}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') fire(e); }}
      style={{
        alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: 4,
        maxWidth: '100%', minHeight: 24, padding: '4px 10px', boxSizing: 'border-box', marginTop: 2,
        borderRadius: 'var(--r-pill)', cursor: 'pointer',
        background: spec.tone && spec.tone !== 'muted' ? toneSoft(spec.tone) : 'var(--surface-2)',
        color: spec.tone && spec.tone !== 'muted' ? toneColor(spec.tone) : 'var(--text-2)',
        fontSize: 12, fontWeight: 500,
      }}
    >
      {spec.icon ? <Icon props={{ name: spec.icon, size: 12, tone: 'accent' }} /> : null}
      <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{spec.label}</span>
    </span>
  );
}

function Media({ spec, ctx, aspect, fallbackFit, rounded }) {
  const src = bindValue(spec.src, ctx);
  // `media.badge` is ONE badge or a LIST of them, and each may claim a corner.
  // It was singular and hard-pinned top-right, so a tile that needed a duration
  // bottom-left AND a state top-right could not say so without an Overlay
  // wrapper that then owned the layout the card was supposed to own.
  const badges = badgeSpecs(spec.badge === undefined ? [] : spec.badge, ctx)
    .map((b) => ({ ...b, region: b.region || 'top-end' }));
  // KIT DEFAULT (§10): a private-session card folds its "🔒 Private" chip onto the HERO IMAGE's
  // own corner layer at bottom-end, so it sits opposite the format badges (DOCX/… at top) instead
  // of over them (user 0908). Flutter twin: canonical.dart injects the same into media['badge'].
  if (cardIsPrivate(ctx)) {
    badges.push({ tone: 'muted', text: '🔒 Private', region: 'bottom-end' });
  }
  return (
    <div style={{ position: 'relative', minWidth: 0 }}>
      <Image props={{
        src: Array.isArray(src) ? src[0] : src,
        fit: spec.fit || fallbackFit || 'cover',
        ratio: ratioValue(spec.ratio || aspect, '1 / 1'),
        // a chrome-less card wants the object on the PAGE, not on a plate
        plate: spec.plate,
        // A picture that runs to the card's own edge takes the CARD's corner:
        // its own radius inside the frame's cut two arcs out of the same
        // corner, and the sliver of surface between them read as a rendering
        // fault. An inset thumbnail keeps its radius — nothing is clipping it.
        rounded,
        // `shape` clips the REAL picture (circle / square / rounded); when the
        // spec sets it, it wins over `rounded`, so a `media:{shape:'circle'}`
        // card (a round meal thumbnail) draws a circular photo. Absent = the
        // `rounded` behavior above, unchanged.
        shape: spec.shape,
        fallback: spec.fallback,
        fallbackKind: spec.fallbackKind,
        fallbackValue: spec.fallbackValue,
        fallbackColor: spec.fallbackColor,
        fallbackShape: spec.fallbackShape,
        fallbackTone: spec.fallbackTone,
        position: spec.position,
        inset: spec.inset,
        lift: spec.lift,
        alt: text(spec.alt, ctx) || undefined,
      }} />
      <CornerBadges badges={badges} ctx={ctx} />
    </div>
  );
}

/**
 * THE POSTER'S MOTION LAYER — a cover that MOVES, over the still it replaces.
 *
 * An authored catalogue mixes stills with clips, and the tile could only ever
 * draw a clip's first frame: two templates that behave completely differently
 * looked identical apart from a chip. `motionSrc` is an ANIMATED IMAGE
 * (animated WebP / GIF), deliberately NOT a video — a rail of ten autoplaying
 * `<video>` elements is ten decoders and a pile of policy (sound, controls, a
 * timeline) for a preview that offers none of it.
 *
 * THREE things turn the motion off, and every one of them lands on the STILL
 * underneath rather than on a hole: a reader who asked for no motion, a tile
 * that is not on screen (an off-screen rail must not animate), and a source
 * that failed to load. The still is always rendered under this layer, so each
 * is a fade-out and never a broken box.
 *
 * The reset on a CHANGED source is the caller's `key`, not an effect here: a
 * new poster is a new layer, which is also what re-arms the load fade.
 */
function PosterMotion({ src, fit }) {
  const host = useRef(null);
  // A runtime with no observer (a test DOM, an old WebView) is not a reason to
  // suppress the animation — it degrades to EAGER, the way `Image` does.
  const [inView, setInView] = useState(() => typeof IntersectionObserver !== 'function');
  const [state, setState] = useState('loading');

  useEffect(() => {
    if (typeof IntersectionObserver !== 'function') return undefined;
    const node = host.current;
    if (!node) return undefined;
    const io = new IntersectionObserver(
      (entries) => { for (const e of entries) setInView(e.isIntersecting); },
      // a margin ahead of the edge, so a tile is already moving by the time it
      // has finished sliding into the rail rather than starting under the eye
      { rootMargin: '96px' },
    );
    io.observe(node);
    return () => io.disconnect();
  }, []);

  return (
    // a DIV, like the scrim beside it: the layers over a poster are boxes, and
    // an inline element inside the card's button is where a badge lives.
    <div
      ref={host}
      aria-hidden
      style={{ position: 'absolute', inset: 0, display: 'block', pointerEvents: 'none' }}
    >
      {inView && state !== 'failed' ? (
        <img
          data-vi-poster-motion=""
          // A data: URI is usually decoded before React binds `onLoad`, so the
          // event never fires and the fade below would stick at 0 — the same
          // trap `Image` catches, caught the same way.
          ref={(node) => { if (node && node.complete && node.naturalWidth > 0) setState('loaded'); }}
          src={src}
          alt=""
          onLoad={() => setState('loaded')}
          onError={() => setState('failed')}
          style={{
            width: '100%', height: '100%', display: 'block',
            objectFit: fit === 'contain' ? 'contain' : 'cover',
            opacity: state === 'loaded' ? 1 : 0,
            transition: 'opacity 220ms var(--ease)',
          }}
        />
      ) : null}
    </div>
  );
}

/** `motionSrc` → the one animated source to draw, or '' for "still only". */
function motionOf(motionSrc) {
  const one = Array.isArray(motionSrc) ? motionSrc[0] : motionSrc;
  if (!one || typeof one !== 'string') return '';
  // SEALED, and the same rule the still carries: a `media://` handle never
  // reaches the DOM — the host resolves it, and until it does there is no
  // motion to draw.
  if (one.startsWith('media://')) return '';
  return prefersReducedMotion() ? '' : one;
}

/**
 * POSTER FACE — the picture-dominant tile, and the kit's missing middle.
 *
 * `bare-media` is the picture with no words (it refuses a title on purpose)
 * and `media-top` is a picture with a CAPTION BLOCK under it. Neither can draw
 * the shape most authored catalogues actually ship: the media fills the frame
 * at a declared aspect and the words are painted ON it, over a bottom scrim.
 * Between "no words" and "words in a white shelf" there was nothing.
 *
 * It is exported because TWO components need the identical face — a `Card` in
 * a collection, and an OPTION in a rail/grid catalogue (`filters.jsx`). A rail
 * that re-implemented the poster would drift from the card version tile by
 * tile, which is exactly how the kit ended up with two spellings of a photo
 * card in the first place.
 *
 * SEALED here, so both callers inherit it: the scrim is the CONTRAST device,
 * not decoration — `scrim: 'auto'` draws it exactly when there is ink to
 * protect and omits it when the poster carries no words at all.
 */
export function PosterFace({
  src, motionSrc, eyebrow, eyebrowTone, title, subtitle, alt,
  aspect, fit, scrim = 'auto', overlayAlign = 'start', rounded = true, corner, children,
}) {
  const hasOverlay = Boolean(eyebrow || title || subtitle);
  const motion = motionOf(motionSrc);
  const drawScrim = scrim === false || scrim === 'none' ? false : (scrim === 'auto' ? hasOverlay : true);
  const align = OVERLAY_ALIGN[overlayAlign] || OVERLAY_ALIGN.start;
  return (
    <div style={{
      position: 'relative', width: '100%', minWidth: 0,
      borderRadius: rounded === false ? 0 : 'var(--r)', overflow: 'hidden',
      background: 'var(--surface-3)',
    }}>
      <Image props={{
        src: Array.isArray(src) ? src[0] : src,
        fit: fit || 'cover',
        ratio: ratioValue(aspect, POSTER_ASPECT),
        // the picture IS the frame here, so it takes the frame's corner rather
        // than cutting a second, smaller arc inside it
        rounded: false,
        alt: alt || undefined,
      }} />
      {/* over the still, UNDER the scrim: the ink stays as readable on a moving
          cover as it is on a fixed one. */}
      {motion ? <PosterMotion key={motion} src={motion} fit={fit} /> : null}
      {drawScrim ? (
        <div aria-hidden style={{
          position: 'absolute', left: 0, right: 0, bottom: 0,
          height: MEDIA_SCRIM_HEIGHT, background: MEDIA_SCRIM, pointerEvents: 'none',
        }} />
      ) : null}
      {hasOverlay ? (
        <div style={{
          position: 'absolute', left: 0, right: 0, bottom: 0, padding: 'var(--sp-3)',
          display: 'flex', flexDirection: 'column', alignItems: align.items,
          textAlign: align.text, gap: 2, minWidth: 0,
        }}>
          {eyebrow ? (
            <span style={{
              ...TYPE_STYLE.eyebrow, letterSpacing: '0.1em',
              color: eyebrowTone ? toneColor(eyebrowTone) : MEDIA_INK_2,
              minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>{eyebrow}</span>
          ) : null}
          {title ? (
            <span style={{
              fontFamily: 'var(--font-display)', fontSize: 'var(--t-lg)', fontWeight: 600,
              lineHeight: 1.08, letterSpacing: '-0.02em', color: MEDIA_INK,
              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
            }}>{title}</span>
          ) : null}
          {subtitle ? (
            <span style={{
              fontSize: 'var(--t-xs)', color: MEDIA_INK_2, lineHeight: 1.3,
              minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>{subtitle}</span>
          ) : null}
        </div>
      ) : null}
      {corner ? (
        <div style={{ position: 'absolute', top: 'var(--sp-2)', right: 'var(--sp-2)' }}>{corner}</div>
      ) : null}
      {children}
    </div>
  );
}

/**
 * The card's outer element (CONTRACT §11). A card whose `activated` is wired to
 * something is a real BUTTON — press affordance, button role, pointer cursor. A
 * card with nothing wired to `activated` — scene-studio's `uploadContext`, and
 * every collection tile whose row-tap the collection itself owns — is NOT a
 * button: a plain box, no press affordance, default cursor, and no trailing
 * chevron (each layout omits it). It KEEPS the click that emits `activated`
 * because a web collection routes a tile's tap THROUGH this handler (unlike
 * Flutter, whose collection wraps its own gesture); a standalone unwired card's
 * emit simply finds no wire and is a harmless no-op. The Flutter twin gates the
 * GestureDetector + chevron on `scope.connected('activated')`.
 */
function CardShell({ activatable, onClick, ariaLabel, style, children }) {
  if (activatable) {
    return (
      <button type="button" onClick={onClick} aria-label={ariaLabel} style={style}>
        {children}
      </button>
    );
  }
  return (
    <div onClick={onClick} style={{ ...style, cursor: 'default' }}>
      {children}
    </div>
  );
}

/**
 * SEALED: the `item` scope, and NO CRUD — the enclosing collection owns it,
 * whatever the row's lifecycle is.
 *
 * `layout` decides the arrangement ONLY — every layout reads the SAME props,
 * so switching a widget from a photo grid to a text list is a one-word
 * blueprint edit rather than a different card component.
 */
export function Card({ variant, props = {}, ctx, emit, connected = () => true, rerouted = false }) {
  const layout = props.layout || variant || 'media-top';
  const media = mediaSpecOf(props.media);
  // A declared media whose bind resolved EMPTY still has a slot when the spec
  // declares a FALLBACK: that is the whole point of the fallback vocabulary —
  // "no picture of this row" should draw the row's plate, not close the hole up
  // and leave the caption sitting on nothing. Without a fallback the old rule
  // stands (an unresolved src draws no media box at all), so nothing that did
  // not ask for one moves. The Flutter twin already read it this way.
  const wantsFallback = props.media !== undefined && Boolean(media.fallback || media.fallbackKind);
  const hasMedia = layout !== 'text' && layout !== 'stat' && (media.src !== undefined || wantsFallback);
  const title = text(props.title, ctx);
  const subtitle = text(props.subtitle, ctx);
  // A right-hand readout on a `media-left` row — the relative time in a feed, a
  // count in a ledger. It TAKES the trailing slot and the chevron steps aside:
  // two things competing for the same edge is what makes a row unreadable.
  const trailing = text(props.trailing, ctx);
  const activate = () => emit?.('activated', {});
  // §11: EFFECTIVE tappability. A card is interactive — a real button, and
  // (media-left) shows the "tap to open" chevron — when its OWN `activated` is
  // wired OR it is a re-routed collection/drill-in tile (the collection routes
  // the row-tap through this handler, e.g. plant-expert `attentionRows` wired
  // via `itemActivated`, not the card's own `activated`). Only a standalone card
  // with NOTHING wired (scene-studio's `uploadContext`) is inert: an inert box,
  // no chevron. `connected` mirrors Flutter's `scope.connected('activated')`;
  // `() => true` default keeps a DIRECT render (a studio preview) interactive.
  const activatable = rerouted || connected('activated');

  // BARE-MEDIA: the picture IS the card. No surface, no shadow, contain-fit —
  // a cut-out sticker sitting on the page background rather than in a frame.
  // Badges still ride the corner (a status dot must survive losing the chrome),
  // and there is deliberately no title row: the whole point is the object.
  if (layout === 'bare-media') {
    return (
      <CardShell
        activatable={activatable}
        onClick={activate}
        ariaLabel={title || undefined}
        style={{ ...resetButton, display: 'block', width: '100%', minWidth: 0, position: 'relative' }}
      >
        <Media spec={{ ...media, fit: media.fit || 'contain', plate: false }} ctx={ctx} aspect={props.aspect} fallbackFit="contain" />
        <BadgeRow badges={props.badges} ctx={ctx} style={{ position: 'absolute', top: 4, right: 4 }} />
        <CornerBadges badges={props.badges} ctx={ctx} card hasMedia={hasMedia} />
      </CardShell>
    );
  }

  // POSTER: the picture fills the card at a declared aspect and the words ride
  // ON it over a scrim. No caption block, no surface under the text — the tile
  // IS the photograph, which is what an authored catalogue of scenes, looks or
  // covers actually looks like.
  if (layout === 'poster') {
    return (
      <CardShell
        activatable={activatable}
        onClick={activate}
        ariaLabel={title || undefined}
        style={{
          ...resetButton, display: 'block', width: '100%', minWidth: 0, position: 'relative',
          borderRadius: 'var(--r)', overflow: 'hidden',
          boxShadow: props.surface === false ? 'none' : 'var(--shadow-1)',
        }}
      >
        <PosterFace
          src={bindValue(media.src, ctx)}
          // the animated cover rides in the MEDIA spec beside the still it
          // covers; the top-level spelling is the one a row-bound poster
          // reaches for (`motionSrc: "item.preview_url"`), and both resolve
          // through the card's own ref resolution, so a data: URI passes
          // straight through.
          motionSrc={bindValue(media.motionSrc !== undefined ? media.motionSrc : props.motionSrc, ctx)}
          fit={media.fit || props.fit}
          alt={text(media.alt, ctx) || title}
          aspect={media.ratio || props.aspect}
          eyebrow={text(props.eyebrow, ctx)}
          eyebrowTone={props.eyebrowTone}
          title={title}
          subtitle={subtitle}
          scrim={props.scrim === undefined ? 'auto' : props.scrim}
          overlayAlign={props.overlayAlign}
          corner={<BadgeRow badges={props.badges} ctx={ctx} />}
        >
          <CornerBadges badges={props.badges} ctx={ctx} card hasMedia={hasMedia} />
        </PosterFace>
      </CardShell>
    );
  }

  // The card's FINISH, and only the finish: a soft raised plate with generous
  // corners, so a list of rows reads as a stack of objects on the page rather
  // than as ruled lines through it. Nothing here is a contract — the fields,
  // the signal and the layouts are unchanged.
  const frame = {
    ...resetButton,
    display: 'flex',
    width: '100%',
    minWidth: 0,
    background: props.surface === false ? 'transparent' : 'var(--surface)',
    boxShadow: props.surface === false ? 'none' : 'var(--shadow-1)',
    borderRadius: 'var(--r-lg)',
    overflow: 'hidden',
    // the origin a CORNER badge is pinned to. Inert with no regioned badge, and
    // the frame's own box is unchanged — `relative` on a static box moves
    // nothing, which is what keeps every existing card pixel-identical.
    position: 'relative',
  };

  const aside = Array.isArray(props.aside)
    ? props.aside.map((part) => text(part, ctx)).filter(Boolean).join(' · ')
    : text(props.aside, ctx);
  const action = actionSpec(props.action, ctx);
  const titleLine = (style) => (title || aside ? (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--sp-2)', minWidth: 0 }}>
      <div style={{ ...style, flex: 1, minWidth: 0 }}>{title}</div>
      {aside ? (
        <span data-card-aside="" style={{ flex: '0 0 auto', fontSize: 'var(--t-xs)', color: 'var(--text-3)', whiteSpace: 'nowrap' }}>{aside}</span>
      ) : null}
    </div>
  ) : null);
  const chevron = activatable && props.chevron !== false;

  if (layout === 'media-left') {
    return (
      <CardShell activatable={activatable} onClick={activate} style={{ ...frame, flexDirection: 'row', alignItems: props.mediaAlign === 'start' ? 'flex-start' : 'center', gap: 'var(--sp-3)', padding: 'var(--sp-3)' }}>
        {hasMedia ? (
          <div style={{ flex: `0 0 ${props.mediaWidth || 56}px`, width: props.mediaWidth || 56 }}>
            <Media spec={media} ctx={ctx} aspect={props.aspect} />
          </div>
        ) : null}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
          {titleLine({ ...TITLE_STYLE, fontSize: 'var(--t-md)', WebkitLineClamp: 1 })}
          {subtitle ? <div style={subtitleStyle(props.subtitleLines)}>{subtitle}</div> : null}
          <BadgeRow badges={props.badges} ctx={ctx} />
          {action ? <ActionPill spec={action} emit={emit} /> : null}
          <MetaRows meta={props.meta} ctx={ctx} />
        </div>
        {/* the "tap to open" chevron follows EFFECTIVE tappability (§11): a
            standalone wired card AND a re-routed collection/drill-in tile show
            it; only a standalone card with nothing wired to `activated` (an
            inert box) shows none. `chevron: false` opts a row out; a `trailing`
            readout TAKES the slot and the chevron steps aside. */}
        {trailing
          ? <span style={{ ...SUBTITLE_STYLE, flex: '0 0 auto' }}>{trailing}</span>
          : chevron ? <span aria-hidden style={{ color: 'var(--text-3)', fontSize: 18, flex: '0 0 auto' }}>›</span> : null}
        <CornerBadges badges={props.badges} ctx={ctx} card hasMedia={hasMedia} />
      </CardShell>
    );
  }

  if (layout === 'stat') {
    return (
      <CardShell activatable={activatable} onClick={activate} style={{ ...frame, flexDirection: 'column', gap: 4, padding: 'var(--sp-3)' }}>
        <div style={{ ...TYPE_STYLE.title, fontSize: 'var(--t-2xl)', lineHeight: 1.05 }}>{title || '—'}</div>
        {subtitle ? <div style={{ ...TYPE_STYLE.eyebrow, color: 'var(--text-3)' }}>{subtitle}</div> : null}
        <BadgeRow badges={props.badges} ctx={ctx} />
        <MetaRows meta={props.meta} ctx={ctx} />
        <CornerBadges badges={props.badges} ctx={ctx} card hasMedia={hasMedia} />
      </CardShell>
    );
  }

  if (layout === 'text') {
    return (
      <CardShell activatable={activatable} onClick={activate} style={{ ...frame, flexDirection: 'column', gap: 4, padding: 'var(--sp-3)', alignItems: 'flex-start' }}>
        {aside ? <div style={{ alignSelf: 'stretch', minWidth: 0 }}>{titleLine(TITLE_STYLE)}</div> : (title ? <div style={TITLE_STYLE}>{title}</div> : null)}
        {subtitle ? <div style={subtitleStyle(props.subtitleLines)}>{subtitle}</div> : null}
        <BadgeRow badges={props.badges} ctx={ctx} />
        {action ? <ActionPill spec={action} emit={emit} /> : null}
        <MetaRows meta={props.meta} ctx={ctx} />
        <CornerBadges badges={props.badges} ctx={ctx} card hasMedia={hasMedia} />
      </CardShell>
    );
  }

  // media-top (the default): the shape a photo grid wants.
  return (
    <CardShell activatable={activatable} onClick={activate} style={{ ...frame, flexDirection: 'column', alignItems: 'stretch' }}>
      {hasMedia ? <Media spec={media} ctx={ctx} aspect={props.aspect} fallbackFit={props.fit} rounded={false} /> : null}
      {title || subtitle || props.badges || props.meta ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: 'var(--sp-2) var(--sp-3) var(--sp-3)', minWidth: 0 }}>
          {title ? <div style={TITLE_STYLE}>{title}</div> : null}
          {subtitle ? <div style={SUBTITLE_STYLE}>{subtitle}</div> : null}
          <BadgeRow badges={props.badges} ctx={ctx} />
          <MetaRows meta={props.meta} ctx={ctx} />
        </div>
      ) : null}
      <CornerBadges badges={props.badges} ctx={ctx} card hasMedia={hasMedia} />
    </CardShell>
  );
}

